import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Upload,
  Plus,
  Trash2,
  Edit2,
  ChevronUp,
  ChevronDown,
  Copy,
  RefreshCw,
  AlertCircle,
  FileText,
  Send,
  RotateCcw,
  Wand2,
  Globe,
  Info,
  Printer,
} from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlassButton from '../ui/GlassButton';
import GlassInput from '../ui/GlassInput';
import GlassSelect from '../ui/GlassSelect';
import GlassTabs from '../ui/GlassTabs';
import GlassBadge from '../ui/GlassBadge';
import ModuleHeader from '../ui/ModuleHeader';
import FormStyleTab from './FormStyleTab';
import {
  formIntakeApi,
  bookingApi,
  BookingTypeItem,
  FormIntakeForm,
  FormIntakeInvite,
  FormIntakeSubmission,
  FormIntakeSectionDef,
  FormIntakeFieldDef,
  FormIntakeSchemaDef,
  FormIntakeProvisioningStatus,
} from '../../services/api';
import FieldDiffModal from './FieldDiffModal';
import { printSubmission } from './submissionPrint';
import './FormIntake.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  return fallback;
};

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `section_${Date.now()}`;

const newFieldId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
];

const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'default',
  provisioning: 'warning',
  published: 'success',
  failed: 'danger',
};

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString() : '—');

// ─── ToggleSwitch ────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, label }) => (
  <div className="fi-toggle-row">
    <label className="fi-toggle-switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="fi-toggle-slider" />
    </label>
    {label && <span className="fi-toggle-label">{label}</span>}
  </div>
);

// ─── UploadTab ───────────────────────────────────────────────────────────────

interface UploadTabProps {
  onUploaded: (form: FormIntakeForm) => void;
  projectId?: string;
}

const UploadTab: React.FC<UploadTabProps> = ({ onUploaded, projectId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setLoading(true);
    try {
      const form = await formIntakeApi.upload(file, projectId);
      onUploaded(form);
    } catch (err: any) {
      setError(getApiError(err, 'Upload failed. Please try again.'));
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <GlassCard className="fi-form-card">
      <div className="fi-empty-state">
        <div className="fi-empty-icon">
          <Upload size={48} />
        </div>
        <h3 className="fi-empty-title">Upload a Form Document</h3>
        <p className="fi-empty-desc">
          Upload a .docx or .xlsx file. The AI will extract sections and fields automatically.
        </p>
        <GlassButton
          variant="primary"
          onClick={() => fileRef.current?.click()}
          disabled={loading}
        >
          {loading ? 'Uploading...' : 'Choose File'}
        </GlassButton>
        <input
          ref={fileRef}
          type="file"
          accept=".docx,.xlsx"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        {error && (
          <div className="fi-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    </GlassCard>
  );
};

// ─── FormsTab ────────────────────────────────────────────────────────────────

interface FormsTabProps {
  forms: FormIntakeForm[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onEdit: (form: FormIntakeForm) => void;
  onInvites: (form: FormIntakeForm) => void;
  onPublicLink: (form: FormIntakeForm) => void;
  onSubmissions: (form: FormIntakeForm) => void;
  onPublishToggle: (form: FormIntakeForm) => Promise<void>;
  onDelete: (form: FormIntakeForm) => Promise<void>;
  onGenerateAndPublish: (form: FormIntakeForm) => Promise<void>;
}

const FormsTab: React.FC<FormsTabProps> = ({
  forms,
  loading,
  error,
  onRefresh,
  onEdit,
  onInvites,
  onPublicLink,
  onSubmissions,
  onPublishToggle,
  onDelete,
  onGenerateAndPublish,
}) => {
  const [publishing, setPublishing] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  // Per-form remote provisioning status (company-target forms only) — gates the publish button.
  const [provStatus, setProvStatus] = useState<Record<string, FormIntakeProvisioningStatus>>({});
  // The company-target form whose field-diff preview modal is open (null = closed).
  const [diffForm, setDiffForm] = useState<FormIntakeForm | null>(null);

  const isCompanyForm = (form: FormIntakeForm) => (form.target_crm ?? 'local') === 'company';

  // Fetch provisioning-status for each company-target form so we can annotate / disable Publish
  // when the customer's CRM companion endpoint isn't installed + enabled yet.
  useEffect(() => {
    let cancelled = false;
    const companyForms = forms.filter(isCompanyForm);
    companyForms.forEach((form) => {
      if (provStatus[form.form_id]) return;
      formIntakeApi
        .getProvisioningStatus(form.form_id)
        .then((status) => {
          if (!cancelled) setProvStatus((prev) => ({ ...prev, [form.form_id]: status }));
        })
        .catch(() => {
          /* leave undefined — publish stays enabled and the modal surfaces any 409/502 */
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms]);

  const handlePublish = async (form: FormIntakeForm) => {
    setPublishing((p) => ({ ...p, [form.form_id]: true }));
    try {
      await onPublishToggle(form);
    } catch (err: any) {
      alert(getApiError(err, 'Action failed.'));
    } finally {
      setPublishing((p) => ({ ...p, [form.form_id]: false }));
    }
  };

  // Company-target Publish: preview the CRM field diff and require explicit confirm before the
  // live rebuild. Confirm runs the real publish (throwing so the modal can surface errors).
  const handleConfirmPublish = async () => {
    if (!diffForm) return;
    await onPublishToggle(diffForm);
  };

  const handleGenerate = async (form: FormIntakeForm) => {
    setGenerating((g) => ({ ...g, [form.form_id]: true }));
    try {
      await onGenerateAndPublish(form);
    } finally {
      setGenerating((g) => ({ ...g, [form.form_id]: false }));
    }
  };

  const handleDelete = async (form: FormIntakeForm) => {
    const confirmed = window.confirm(
      `Delete "${form.name}"? Any outstanding fill links will stop working. This cannot be undone.`
    );
    if (!confirmed) return;
    setDeleting((d) => ({ ...d, [form.form_id]: true }));
    try {
      await onDelete(form);
    } finally {
      setDeleting((d) => ({ ...d, [form.form_id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="fi-empty-state">
        <p className="fi-empty-desc">Loading forms...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fi-error">
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  if (!forms.length) {
    return (
      <div className="fi-empty-state">
        <div className="fi-empty-icon">
          <FileText size={48} />
        </div>
        <h3 className="fi-empty-title">No forms yet</h3>
        <p className="fi-empty-desc">Upload a document to create your first form.</p>
        <GlassButton variant="secondary" onClick={onRefresh}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <GlassButton variant="secondary" onClick={onRefresh}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>
      <div className="fi-forms-grid">
        {forms.map((form) => (
          <GlassCard key={form.form_id} className="fi-form-card">
            <div className="fi-form-card-header">
              <h3 className="fi-form-card-title">{form.name}</h3>
              <GlassBadge variant={STATUS_VARIANTS[form.status] ?? 'default'}>
                {form.status}
              </GlassBadge>
            </div>
            <div className="fi-form-card-meta">
              <span>{form.schema?.sections?.length ?? 0} sections</span>
              <span>{form.submission_count ?? 0} response{(form.submission_count ?? 0) === 1 ? '' : 's'}</span>
              {form.created_at && <span>Created {fmtDate(form.created_at)}</span>}
            </div>
            <div className="fi-form-card-actions">
              <GlassButton variant="secondary" onClick={() => onEdit(form)}>
                <Edit2 size={14} /> Edit
              </GlassButton>
              <GlassButton variant="secondary" onClick={() => onInvites(form)}>
                <Send size={14} /> Invites
              </GlassButton>
              <GlassButton variant="secondary" onClick={() => onPublicLink(form)}>
                <Globe size={14} /> Public Link
              </GlassButton>
              <GlassButton variant="secondary" onClick={() => onSubmissions(form)}>
                <FileText size={14} /> Submissions
              </GlassButton>
              {(form.status === 'draft' || form.status === 'failed' || form.status === 'provisioning') && (
                <div className="fi-generate-group">
                  <GlassButton
                    variant="primary"
                    onClick={() => handleGenerate(form)}
                    disabled={generating[form.form_id] || form.status === 'provisioning'}
                  >
                    {(generating[form.form_id] || form.status === 'provisioning') ? (
                      <><RefreshCw size={14} className="fi-spin" /> Generating...</>
                    ) : (
                      <><Wand2 size={14} /> Generate CRM &amp; Publish</>
                    )}
                  </GlassButton>
                  <span className="fi-generate-caption">Auto-creates CRM modules &amp; publishes — no mapping needed.</span>
                </div>
              )}
              {form.status === 'published' ? (
                <GlassButton
                  variant="danger"
                  onClick={() => handlePublish(form)}
                  disabled={publishing[form.form_id]}
                >
                  {publishing[form.form_id] ? 'Unpublishing...' : 'Unpublish'}
                </GlassButton>
              ) : (() => {
                const company = isCompanyForm(form);
                const status = provStatus[form.form_id];
                const remoteNotReady = company && !!status && status.remote_ready === false;
                const busy =
                  publishing[form.form_id] || form.status === 'provisioning' || generating[form.form_id];
                return (
                  <div className="fi-generate-group">
                    <GlassButton
                      variant="primary"
                      // Company-target forms preview the CRM field diff + confirm before the live
                      // rebuild; local forms publish directly (unchanged).
                      onClick={() => (company ? setDiffForm(form) : handlePublish(form))}
                      disabled={busy || remoteNotReady}
                      title={
                        remoteNotReady
                          ? status?.reason ?? 'CRM publishing not configured for your instance yet'
                          : undefined
                      }
                    >
                      {publishing[form.form_id] || form.status === 'provisioning'
                        ? 'Provisioning...'
                        : 'Publish'}
                    </GlassButton>
                    {remoteNotReady && (
                      <span className="fi-remote-not-ready">
                        <Info size={12} />
                        {status?.reason
                          ? `CRM publishing not available yet — ${status.reason}.`
                          : 'CRM publishing not configured for your instance yet — contact support.'}
                      </span>
                    )}
                  </div>
                );
              })()}
              <GlassButton
                variant="danger"
                onClick={() => handleDelete(form)}
                disabled={deleting[form.form_id] || form.deletable === false}
                title={
                  form.deletable === false
                    ? `Can't delete — ${form.submission_count ?? 0} response(s) captured (data is also in SuiteCRM)`
                    : 'Delete this form'
                }
                className={form.deletable === false ? 'fi-btn-disabled-danger' : ''}
              >
                <Trash2 size={14} /> {deleting[form.form_id] ? 'Deleting...' : 'Delete'}
              </GlassButton>
            </div>
          </GlassCard>
        ))}
      </div>

      {diffForm && (
        <FieldDiffModal
          form={diffForm}
          onConfirm={handleConfirmPublish}
          onClose={() => setDiffForm(null)}
        />
      )}
    </div>
  );
};

// ─── FieldEditor ─────────────────────────────────────────────────────────────

interface FieldEditorProps {
  field: FormIntakeFieldDef;
  onChange: (f: FormIntakeFieldDef) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const needsOptions = ['select', 'radio', 'checkbox'].includes(field.type);

  return (
    <div>
      <div className="fi-field-row">
        <GlassSelect
          value={field.type}
          onChange={(v) => onChange({ ...field, type: v })}
          options={FIELD_TYPES}
          label="Type"
        />
        <GlassInput
          label="Label"
          value={field.label}
          onChange={(v) => onChange({ ...field, label: v })}
          placeholder="Field label"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <label className="fi-field-required">
            <input
              type="checkbox"
              checked={!!field.required}
              onChange={(e) => onChange({ ...field, required: e.target.checked })}
            />
            Required
          </label>
        </div>
        <div className="fi-field-controls">
          <GlassButton variant="ghost" onClick={onMoveUp} disabled={isFirst} title="Move up">
            <ChevronUp size={14} />
          </GlassButton>
          <GlassButton variant="ghost" onClick={onMoveDown} disabled={isLast} title="Move down">
            <ChevronDown size={14} />
          </GlassButton>
          <GlassButton variant="ghost" onClick={onRemove} title="Remove field">
            <Trash2 size={14} />
          </GlassButton>
        </div>
      </div>
      <div style={{ marginTop: 'var(--space-2)' }}>
        <GlassInput
          label="Help text (optional)"
          value={field.help ?? ''}
          onChange={(v) => onChange({ ...field, help: v })}
          placeholder="Per-field guidance shown to clients beneath this field"
        />
      </div>
      {needsOptions && (
        <div className="fi-field-options">
          <GlassInput
            label="Options (comma-separated)"
            value={(field.options ?? []).join(', ')}
            onChange={(v) =>
              onChange({
                ...field,
                options: v.split(',').map((o) => o.trim()).filter(Boolean),
              })
            }
            placeholder="Option 1, Option 2, Option 3"
          />
        </div>
      )}
    </div>
  );
};

// ─── SectionEditor ───────────────────────────────────────────────────────────

interface SectionEditorProps {
  section: FormIntakeSectionDef;
  onChange: (s: FormIntakeSectionDef) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) => {
  const addField = () => {
    const newField: FormIntakeFieldDef = {
      id: newFieldId(),
      type: 'text',
      label: '',
      required: false,
    };
    onChange({ ...section, fields: [...section.fields, newField] });
  };

  const updateField = (idx: number, f: FormIntakeFieldDef) => {
    const fields = [...section.fields];
    fields[idx] = f;
    onChange({ ...section, fields });
  };

  const removeField = (idx: number) => {
    onChange({ ...section, fields: section.fields.filter((_, i) => i !== idx) });
  };

  const moveField = (idx: number, dir: -1 | 1) => {
    const fields = [...section.fields];
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    [fields[idx], fields[target]] = [fields[target], fields[idx]];
    onChange({ ...section, fields });
  };

  return (
    <div className="fi-section-block">
      <div className="fi-section-header">
        <div className="fi-section-title-row">
          <GlassInput
            label="Section Title"
            value={section.title}
            onChange={(v) =>
              onChange({ ...section, title: v, key: section.key || slugify(v) })
            }
            placeholder="Section title"
          />
          <ToggleSwitch
            checked={section.repeating}
            onChange={(v) => onChange({ ...section, repeating: v })}
            label="Repeating"
          />
          {!isFirst && (
            <ToggleSwitch
              checked={!!section.page_break_before}
              onChange={(v) => onChange({ ...section, page_break_before: v })}
              label="Page break before this section"
            />
          )}
          {section.repeating && (
            <GlassInput
              label="Item Label"
              value={section.item_label ?? ''}
              onChange={(v) => onChange({ ...section, item_label: v })}
              placeholder="e.g. Person"
            />
          )}
          <span
            style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
          >
            key: {section.key}
          </span>
        </div>
        <div className="fi-section-controls">
          <GlassButton variant="ghost" onClick={onMoveUp} disabled={isFirst} title="Move section up">
            <ChevronUp size={14} />
          </GlassButton>
          <GlassButton variant="ghost" onClick={onMoveDown} disabled={isLast} title="Move section down">
            <ChevronDown size={14} />
          </GlassButton>
          <GlassButton variant="ghost" onClick={onRemove} title="Remove section">
            <Trash2 size={14} />
          </GlassButton>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <label style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium, 500)' }}>
          Description (optional)
        </label>
        <textarea
          className="fi-description-textarea"
          value={section.description ?? ''}
          onChange={(e) => onChange({ ...section, description: e.target.value })}
          placeholder="Instructional text shown to clients under this section heading…"
          rows={2}
        />
      </div>

      <div className="fi-field-list">
        {section.fields.map((field, idx) => (
          <FieldEditor
            key={field.id}
            field={field}
            onChange={(f) => updateField(idx, f)}
            onRemove={() => removeField(idx)}
            onMoveUp={() => moveField(idx, -1)}
            onMoveDown={() => moveField(idx, 1)}
            isFirst={idx === 0}
            isLast={idx === section.fields.length - 1}
          />
        ))}
      </div>
      <GlassButton variant="secondary" onClick={addField}>
        <Plus size={14} /> Add Field
      </GlassButton>
    </div>
  );
};

// ─── EditorTab ───────────────────────────────────────────────────────────────

interface EditorTabProps {
  form: FormIntakeForm;
  onSaved: (form: FormIntakeForm) => void;
}

const EditorTab: React.FC<EditorTabProps> = ({ form, onSaved }) => {
  const [name, setName] = useState(form.name);
  const [sections, setSections] = useState<FormIntakeSectionDef[]>(
    form.schema?.sections ?? []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedKeys, setSuggestedKeys] = useState<string[] | null>(null);
  const [suggestSource, setSuggestSource] = useState<'gemini' | 'heuristic' | null>(null);

  useEffect(() => {
    setName(form.name);
    setSections(form.schema?.sections ?? []);
    setError('');
    setSuccess('');
    setSuggestedKeys(null);
  }, [form.form_id]);

  const handleSuggestPageBreaks = async () => {
    setSuggesting(true);
    setError('');
    try {
      const res = await formIntakeApi.suggestPageBreaks(form.form_id);
      setSuggestedKeys(res.suggested_keys);
      setSuggestSource(res.source);
    } catch (err: any) {
      setError(getApiError(err, 'Could not generate suggestions.'));
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestions = () => {
    if (!suggestedKeys) return;
    const keySet = new Set(suggestedKeys);
    setSections((prev) => prev.map((s) => (keySet.has(s.key) ? { ...s, page_break_before: true } : s)));
    setSuggestedKeys(null);
  };

  const addSection = () => {
    const key = `section_${Date.now()}`;
    setSections((prev) => [
      ...prev,
      { key, title: '', repeating: false, fields: [] },
    ]);
  };

  const updateSection = (idx: number, s: FormIntakeSectionDef) => {
    setSections((prev) => {
      const next = [...prev];
      next[idx] = s;
      return next;
    });
  };

  const removeSection = (idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveSection = (idx: number, dir: -1 | 1) => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const isPublished = form.status === 'published';

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (isPublished) {
        // Published forms can't have their schema edited here (update_form 409s) — only the
        // page-break flags ride through /presentation, which is a strict key-matched merge on
        // the existing schema, never a wholesale sections replacement. Field/name/section
        // changes on a published form require unpublishing first.
        const page_breaks: Record<string, boolean> = {};
        sections.forEach((s) => { page_breaks[s.key] = !!s.page_break_before; });
        const updated = await formIntakeApi.updatePresentation(form.form_id, { page_breaks });
        setSuccess('Page breaks saved.');
        onSaved(updated);
      } else {
        const schema: FormIntakeSchemaDef = { name, sections };
        const updated = await formIntakeApi.updateForm(form.form_id, { name, schema });
        setSuccess('Saved successfully.');
        onSaved(updated);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fi-editor">
      {isPublished && (
        <div className="fi-info-banner">
          <Info size={14} />
          This form is published — field/name changes are locked (unpublish to edit them). Page
          breaks can still be adjusted here and saved without unpublishing.
        </div>
      )}
      <GlassCard>
        <GlassInput
          label="Form Name"
          value={name}
          onChange={(v) => setName(v)}
          placeholder="Form name"
          disabled={isPublished}
        />
      </GlassCard>

      {sections.length > 1 && (
        <GlassButton variant="secondary" onClick={handleSuggestPageBreaks} disabled={suggesting}>
          <Wand2 size={14} /> {suggesting ? 'Thinking…' : 'Suggest Page Breaks'}
        </GlassButton>
      )}

      {suggestedKeys !== null && (
        <GlassCard className="fi-suggest-panel">
          {suggestedKeys.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              No page-break suggestions — this form's sections don't show an obvious grouping pattern.
            </p>
          ) : (
            <>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Suggested page breaks before ({suggestSource === 'gemini' ? 'AI-suggested' : 'based on numbered headings'}):
              </p>
              <ul className="fi-suggest-list">
                {suggestedKeys.map((k) => {
                  const s = sections.find((sec) => sec.key === k);
                  return <li key={k}>{s?.title || k}</li>;
                })}
              </ul>
            </>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <GlassButton variant="secondary" onClick={() => setSuggestedKeys(null)}>
              Dismiss
            </GlassButton>
            {suggestedKeys.length > 0 && (
              <GlassButton variant="primary" onClick={applySuggestions}>
                Apply Suggestions
              </GlassButton>
            )}
          </div>
        </GlassCard>
      )}

      {sections.map((section, idx) => (
        <SectionEditor
          key={section.key + idx}
          section={section}
          onChange={(s) => updateSection(idx, s)}
          onRemove={() => removeSection(idx)}
          onMoveUp={() => moveSection(idx, -1)}
          onMoveDown={() => moveSection(idx, 1)}
          isFirst={idx === 0}
          isLast={idx === sections.length - 1}
        />
      ))}

      <GlassButton variant="secondary" onClick={addSection} disabled={isPublished}>
        <Plus size={14} /> Add Section
      </GlassButton>

      {error && (
        <div className="fi-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      {success && (
        <div style={{ color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)' }}>
          {success}
        </div>
      )}

      <GlassButton variant="primary" onClick={save} disabled={saving}>
        {saving ? 'Saving...' : isPublished ? 'Save Presentation' : 'Save Schema'}
      </GlassButton>
    </div>
  );
};

// ─── CRMTab ──────────────────────────────────────────────────────────────────

interface CRMTabProps {
  form: FormIntakeForm;
  onSaved: (form: FormIntakeForm) => void;
}

const CRMTab: React.FC<CRMTabProps> = ({ form, onSaved }) => {
  const [parentModule, setParentModule] = useState(form.crm_target?.parent_module ?? '');
  const [targetCrm, setTargetCrm] = useState(form.target_crm ?? 'local');
  const [groups, setGroups] = useState<Record<string, { module: string; parent_link_field: string }>>(
    form.crm_target?.groups ?? {}
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sections = form.schema?.sections ?? [];
  // Phase 0 leak fix: forms owned by a company with an active CRMInstance are created as
  // target_crm='company' by the backend, which also rejects any attempt to switch them back to
  // 'local' (409). Lock the selector to Company CRM so those users can't try to point their
  // estate/PII schema at the shared local box. Individuals stay 'local' and keep the choice.
  const lockedToCompany = (form.target_crm ?? 'local') === 'company';

  useEffect(() => {
    setParentModule(form.crm_target?.parent_module ?? '');
    setTargetCrm(form.target_crm ?? 'local');
    setGroups(form.crm_target?.groups ?? {});
    setError('');
    setSuccess('');
  }, [form.form_id]);

  const updateGroup = (key: string, field: 'module' | 'parent_link_field', value: string) => {
    setGroups((prev) => ({
      ...prev,
      [key]: {
        module: prev[key]?.module ?? '',
        parent_link_field: prev[key]?.parent_link_field ?? '',
        [field]: value,
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const crm_target = { parent_module: parentModule, groups };
      const updated = await formIntakeApi.updateForm(form.form_id, { crm_target, target_crm: targetCrm });
      setSuccess('CRM mapping saved.');
      onSaved(updated);
    } catch (err: any) {
      setError(getApiError(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fi-crm-mapping">
      <GlassCard>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <GlassSelect
            label="Target CRM"
            value={targetCrm}
            onChange={(v) => setTargetCrm(v)}
            disabled={lockedToCompany}
            options={
              lockedToCompany
                ? [{ value: 'company', label: 'Company CRM' }]
                : [
                    { value: 'local', label: 'Local SuiteCRM' },
                    { value: 'company', label: 'Company CRM' },
                  ]
            }
          />
          {lockedToCompany && (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
              Your organisation's forms provision to your own company CRM and can't be routed to the
              shared local instance.
            </div>
          )}
          <GlassInput
            label="Parent Module"
            value={parentModule}
            onChange={(v) => setParentModule(v)}
            placeholder="e.g. Accounts"
          />
        </div>
      </GlassCard>

      {sections.length > 0 && (
        <GlassCard>
          <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Section Mappings
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {sections.map((section) => {
              if (!section.repeating) {
                return (
                  <div key={section.key} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text)' }}>{section.title || section.key}</strong>
                    {' '}&rarr; maps to parent module ({parentModule || 'not set'})
                  </div>
                );
              }
              return (
                <div key={section.key} className="fi-crm-section-row">
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', paddingBottom: '6px' }}>
                    {section.title || section.key}
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                      (repeating)
                    </div>
                  </div>
                  <GlassInput
                    label="CRM Module"
                    value={groups[section.key]?.module ?? ''}
                    onChange={(v) => updateGroup(section.key, 'module', v)}
                    placeholder="e.g. Contacts"
                  />
                  <GlassInput
                    label="Parent Link Field"
                    value={groups[section.key]?.parent_link_field ?? ''}
                    onChange={(v) => updateGroup(section.key, 'parent_link_field', v)}
                    placeholder="e.g. account_id"
                  />
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {error && (
        <div className="fi-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      {success && (
        <div style={{ color: 'var(--success, #16a34a)', fontSize: 'var(--text-sm)' }}>
          {success}
        </div>
      )}

      <GlassButton variant="primary" onClick={save} disabled={saving}>
        {saving ? 'Saving...' : 'Save CRM Mapping'}
      </GlassButton>
    </div>
  );
};

// ─── InvitesTab ──────────────────────────────────────────────────────────────

interface InvitesTabProps {
  form: FormIntakeForm;
}

const InvitesTab: React.FC<InvitesTabProps> = ({ form }) => {
  const [invites, setInvites] = useState<FormIntakeInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newInvite, setNewInvite] = useState<FormIntakeInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const [clientLabel, setClientLabel] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [accessPin, setAccessPin] = useState('');

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await formIntakeApi.listInvites(form.form_id);
      setInvites(data);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load invites.'));
    } finally {
      setLoading(false);
    }
  }, [form.form_id]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleCreate = async () => {
    setCreating(true);
    setCreateError('');
    setNewInvite(null);
    try {
      const invite = await formIntakeApi.createInvite(form.form_id, {
        client_label: clientLabel || undefined,
        client_email: clientEmail || undefined,
        expires_in_days: expiresInDays ? parseInt(expiresInDays, 10) : undefined,
        access_pin: accessPin || undefined,
      });
      setNewInvite(invite);
      setClientLabel('');
      setClientEmail('');
      setExpiresInDays('30');
      setAccessPin('');
      await loadInvites();
    } catch (err: any) {
      setCreateError(getApiError(err, 'Failed to create invite.'));
    } finally {
      setCreating(false);
    }
  };

  const inviteUrl = (invite: FormIntakeInvite) =>
    `${window.location.origin}${invite.fill_path}`;

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <GlassCard>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
          Create Invite Link
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <GlassInput
            label="Client Label (optional)"
            value={clientLabel}
            onChange={(v) => setClientLabel(v)}
            placeholder="e.g. Acme Corp"
          />
          <GlassInput
            label="Client Email (optional)"
            value={clientEmail}
            onChange={(v) => setClientEmail(v)}
            placeholder="client@example.com"
          />
          <GlassInput
            label="Expires In Days"
            value={expiresInDays}
            onChange={(v) => setExpiresInDays(v)}
            placeholder="30"
          />
          <GlassInput
            label="Access PIN (optional)"
            value={accessPin}
            onChange={(v) => setAccessPin(v)}
            placeholder="Leave blank for no PIN"
          />
          {createError && (
            <div className="fi-error">
              <AlertCircle size={16} />
              {createError}
            </div>
          )}
          <GlassButton variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create Invite Link'}
          </GlassButton>
        </div>
        {newInvite && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
              Invite link created:
            </div>
            <div className="fi-invite-row">
              <div className="fi-invite-url">{inviteUrl(newInvite)}</div>
              <GlassButton variant="secondary" onClick={() => copyUrl(inviteUrl(newInvite))}>
                <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
              </GlassButton>
            </div>
          </div>
        )}
      </GlassCard>

      <GlassCard>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
          Existing Invites
        </h4>
        {loading ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Loading...</p>
        ) : error ? (
          <div className="fi-error"><AlertCircle size={16} />{error}</div>
        ) : invites.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>No invites yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {invites.map((inv) => (
              <div key={inv.invite_id ?? inv.token} style={{ borderBottom: '1px solid var(--stroke)', paddingBottom: 'var(--space-3)' }}>
                <div className="fi-invite-row">
                  <div>
                    {inv.client_label && (
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                        {inv.client_label}
                      </div>
                    )}
                    {inv.client_email && (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                        {inv.client_email}
                      </div>
                    )}
                    {inv.expires_at && (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                        Expires: {fmtDate(inv.expires_at)}
                      </div>
                    )}
                  </div>
                  <GlassButton variant="ghost" onClick={() => copyUrl(inviteUrl(inv))} title="Copy URL">
                    <Copy size={14} />
                  </GlassButton>
                </div>
                <div className="fi-invite-url" style={{ marginTop: 'var(--space-2)' }}>
                  {inviteUrl(inv)}
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
};

// ─── SubmissionsTab ──────────────────────────────────────────────────────────

interface SubmissionsTabProps {
  form: FormIntakeForm;
}

// Field id -> label lookup, built once per schema (same ids are reused across repeating-section items).
const buildFieldLabels = (schema?: FormIntakeSchemaDef): Record<string, string> => {
  const map: Record<string, string> = {};
  (schema?.sections || []).forEach((sec) => {
    (sec.fields || []).forEach((f) => {
      map[f.id] = f.label || f.id;
    });
  });
  return map;
};

// Best-effort "who is this" label from the answers themselves, for staff/campaign submissions
// that have no invite to carry a client_label. Looks for a field whose label reads like a name.
const guessIdentity = (data: any, fieldLabels: Record<string, string>): { name: string | null; email: string | null } => {
  if (!data || typeof data !== 'object') return { name: null, email: null };
  const nameRe = /\b(full\s*name|your\s*name|^name$|client\s*name)\b/i;
  const emailRe = /\bemail\b/i;
  let name: string | null = null;
  let email: string | null = null;
  const visit = (val: any) => {
    if (name && email) return;
    if (Array.isArray(val)) {
      val.forEach(visit);
    } else if (val && typeof val === 'object') {
      Object.entries(val).forEach(([fieldId, fieldVal]) => {
        if (typeof fieldVal !== 'string' || !fieldVal.trim()) return;
        const label = fieldLabels[fieldId] || fieldId;
        if (!name && nameRe.test(label)) name = fieldVal.trim();
        if (!email && emailRe.test(label)) email = fieldVal.trim();
      });
    }
  };
  Object.values(data).forEach(visit);
  return { name, email };
};

// "Submitted by": the person's own name from the answers wins (an invite label such as the generic
// "Saved for later (self-service)" says nothing about who filled it in); then a staff-set invite
// label, the invite's email, and finally an email found in the answers.
const submittedByLabel = (sub: FormIntakeSubmission, fieldLabels: Record<string, string>): string | null => {
  const { name, email } = guessIdentity(sub.data, fieldLabels);
  if (name) return email ? `${name} (${email})` : name;
  return sub.invite_client_label || sub.invite_client_email || email || null;
};

const SubmissionDetail: React.FC<{ sub: FormIntakeSubmission; schema?: FormIntakeSchemaDef }> = ({ sub, schema }) => {
  const sections = schema?.sections || [];
  const data = sub.data || {};
  const rowStyle: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--text)' };
  const labelStyle: React.CSSProperties = { color: 'var(--text-secondary)', marginRight: 'var(--space-2)' };

  const renderItem = (fields: FormIntakeFieldDef[], item: Record<string, any>, key: React.Key) => (
    <div key={key} style={{ marginBottom: 'var(--space-2)' }}>
      {fields.map((f) => {
        const val = item?.[f.id];
        if (val === undefined || val === null || val === '') return null;
        return (
          <div key={f.id} style={rowStyle}>
            <span style={labelStyle}>{f.label || f.id}:</span>
            <span>{Array.isArray(val) ? val.join(', ') : String(val)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {sections.map((sec) => {
        const secData = data[sec.key];
        if (secData === undefined || secData === null) return null;
        const items = sec.repeating ? (Array.isArray(secData) ? secData : []) : [secData];
        if (items.length === 0) return null;
        return (
          <div key={sec.key} style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', color: 'var(--text)', marginBottom: 'var(--space-1)' }}>
              {sec.title || sec.key}
            </div>
            {items.map((item, idx) => renderItem(sec.fields || [], item, idx))}
          </div>
        );
      })}
      {(sub.crm_parent_record_id || (sub.crm_child_record_ids && Object.keys(sub.crm_child_record_ids).length > 0)) && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
          Synced to CRM — parent record: {sub.crm_parent_record_id ?? '—'}. Open the CRM tab's mapped module in CRM/Leads to view it there.
        </div>
      )}
    </div>
  );
};

const SubmissionsTab: React.FC<SubmissionsTabProps> = ({ form }) => {
  const [submissions, setSubmissions] = useState<FormIntakeSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resyncing, setResyncing] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const fieldLabels = React.useMemo(() => buildFieldLabels(form.schema), [form.schema]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formIntakeApi.listSubmissions(form.form_id);
      setSubmissions(data);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load submissions.'));
    } finally {
      setLoading(false);
    }
  }, [form.form_id]);

  useEffect(() => {
    load();
  }, [load]);

  const resync = async (sid: string) => {
    setResyncing((r) => ({ ...r, [sid]: true }));
    try {
      await formIntakeApi.resyncSubmission(form.form_id, sid);
      await load();
    } catch {
      // ignore — table will show current state
    } finally {
      setResyncing((r) => ({ ...r, [sid]: false }));
    }
  };

  return (
    <GlassCard>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <h4 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
          Submissions
        </h4>
        <GlassButton variant="secondary" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </GlassButton>
      </div>

      {loading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Loading...</p>
      ) : error ? (
        <div className="fi-error"><AlertCircle size={16} />{error}</div>
      ) : submissions.length === 0 ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>No submissions yet.</p>
      ) : (
        <div className="fi-submissions-table-wrapper">
          <table className="fi-submissions-table">
            <thead>
              <tr>
                <th>Submitted By</th>
                <th>Source</th>
                <th>Submitted</th>
                <th>CRM Synced</th>
                <th>Sync Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => {
                const who = submittedByLabel(sub, fieldLabels);
                const isOpen = expanded === sub.submission_id;
                return (
                  <React.Fragment key={sub.submission_id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : sub.submission_id)}
                          style={{
                            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: who ? 'var(--text)' : 'var(--text-secondary)',
                            textDecoration: 'underline', fontSize: 'var(--text-sm)', textAlign: 'left',
                          }}
                          title="Click to view the full submission"
                        >
                          {who || 'View answers'}
                        </button>
                      </td>
                      <td>{sub.source ?? '—'}</td>
                      <td>
                        {fmtDate(sub.submitted_at)}
                        {sub.updated_at && (
                          <div
                            style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}
                            title="A repeat submission from the same email was merged into this one; the previous version is kept."
                          >
                            Updated {fmtDate(sub.updated_at)}
                            {sub.revision_count ? ` · ${sub.revision_count} earlier version${sub.revision_count === 1 ? '' : 's'}` : ''}
                          </div>
                        )}
                      </td>
                      <td>{sub.crm_synced_at ? fmtDate(sub.crm_synced_at) : '—'}</td>
                      <td style={{ color: sub.crm_sync_error ? 'var(--danger)' : undefined }}>
                        {sub.crm_sync_error ?? '—'}
                      </td>
                      <td>
                        <GlassButton
                          variant="ghost"
                          onClick={() => resync(sub.submission_id)}
                          disabled={resyncing[sub.submission_id]}
                          title="Re-sync to CRM"
                        >
                          <RotateCcw size={14} />
                        </GlassButton>
                        <GlassButton
                          variant="ghost"
                          onClick={() => printSubmission(form.name, form.schema, sub, who)}
                          title="Export / print this submission"
                        >
                          <Printer size={14} />
                        </GlassButton>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--panel-strong)', padding: 'var(--space-3)' }}>
                          <SubmissionDetail sub={sub} schema={form.schema} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
};

// ─── PublicLinkTab ───────────────────────────────────────────────────────────

interface PublicLinkTabProps {
  form: FormIntakeForm;
  onUpdated: (form: FormIntakeForm) => void;
}

const PublicLinkTab: React.FC<PublicLinkTabProps> = ({ form, onUpdated }) => {
  const [leadSource, setLeadSource] = useState(form.lead_source ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState('');

  const toggleMerge = async (on: boolean) => {
    setMergeSaving(true);
    setMergeError('');
    try {
      onUpdated(await formIntakeApi.updateSettings(form.form_id, { merge_repeat_submissions: on }));
    } catch (err: any) {
      setMergeError(getApiError(err, 'Failed to update the setting.'));
    } finally {
      setMergeSaving(false);
    }
  };

  // Booking type selector
  const [bookingTypes, setBookingTypes] = useState<BookingTypeItem[]>([]);
  const [bookingTypesLoading, setBookingTypesLoading] = useState(false);
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState<string>(form.booking_type_id ?? '');
  const [bookingSaving, setBookingSaving] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState('');

  useEffect(() => {
    setBookingTypesLoading(true);
    bookingApi.listTypes().then(setBookingTypes).catch(() => {}).finally(() => setBookingTypesLoading(false));
  }, []);

  const handleBookingTypeChange = async (value: string) => {
    setSelectedBookingTypeId(value);
    setBookingSaving(true);
    setBookingError('');
    setBookingSuccess('');
    try {
      const updated = await formIntakeApi.updateForm(form.form_id, { booking_type_id: value || null });
      onUpdated(updated);
      setBookingSuccess('Booking preference saved.');
      setTimeout(() => setBookingSuccess(''), 3000);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setBookingError(typeof detail === 'string' ? detail : 'Failed to save booking preference.');
    } finally {
      setBookingSaving(false);
    }
  };

  const publicUrl = form.public_path
    ? `${window.location.origin}${form.public_path}`
    : '';

  const embedSnippet = publicUrl
    ? `<iframe src="${publicUrl}" style="width:100%;min-height:900px;border:0" title="${form.name}"></iframe>`
    : '';

  const copyText = (text: string, setCopied: (v: boolean) => void) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleEnable = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await formIntakeApi.enablePublicLink(form.form_id, leadSource || undefined);
      onUpdated(updated);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to enable public link.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await formIntakeApi.disablePublicLink(form.form_id);
      onUpdated(updated);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to disable public link.'));
    } finally {
      setSaving(false);
    }
  };

  if (form.status !== 'published') {
    return (
      <GlassCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', color: 'var(--text-secondary)' }}>
          <Globe size={20} />
          <span style={{ fontSize: 'var(--text-sm)' }}>
            Publish the form first to enable the public / website embed link.
          </span>
        </div>
      </GlassCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {!form.public_enabled ? (
        <GlassCard>
          <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
            Publish to Website
          </h4>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: '0 0 var(--space-4)' }}>
            Generate a public link that anyone can use to submit this form — ideal for embedding on your website or sharing in campaigns.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <GlassInput
              label="Campaign / Lead Source (optional)"
              value={leadSource}
              onChange={(v) => setLeadSource(v)}
              placeholder="e.g. website-contact, google-ads"
            />
            {error && (
              <div className="fi-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            <GlassButton variant="primary" onClick={handleEnable} disabled={saving}>
              <Globe size={16} />
              {saving ? 'Enabling...' : 'Publish to Website'}
            </GlassButton>
          </div>
        </GlassCard>
      ) : (
        <>
          <GlassCard>
            <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
              Public Link
            </h4>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
              Share this URL directly or embed it on your website:
            </div>
            <div className="fi-invite-row">
              <div className="fi-invite-url">{publicUrl}</div>
              <GlassButton variant="secondary" onClick={() => copyText(publicUrl, setCopiedUrl)}>
                <Copy size={14} /> {copiedUrl ? 'Copied!' : 'Copy'}
              </GlassButton>
            </div>
            {form.lead_source && (
              <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                Lead source: <strong style={{ color: 'var(--text)' }}>{form.lead_source}</strong>
              </div>
            )}
          </GlassCard>

          <GlassCard>
            <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
              Website Embed
            </h4>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
              Paste this snippet into your website HTML:
            </div>
            <div className="fi-embed-box">{embedSnippet}</div>
            <div style={{ marginTop: 'var(--space-2)' }}>
              <GlassButton variant="secondary" onClick={() => copyText(embedSnippet, setCopiedEmbed)}>
                <Copy size={14} /> {copiedEmbed ? 'Copied!' : 'Copy Embed Code'}
              </GlassButton>
            </div>
          </GlassCard>

          <GlassCard>
            {error && (
              <div className="fi-error" style={{ marginBottom: 'var(--space-3)' }}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            <GlassButton variant="secondary" onClick={handleDisable} disabled={saving}>
              {saving ? 'Disabling...' : 'Disable Public Link'}
            </GlassButton>
          </GlassCard>
        </>
      )}

      {/* Booking type integration */}
      <GlassCard>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
          Offer a booking after submission
        </h4>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: '0 0 var(--space-3)' }}>
          When a client submits this form they will be prompted to book a consultation.
        </p>
        {bookingTypesLoading ? (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Loading booking types...</div>
        ) : (
          <GlassSelect
            label="Booking Type"
            value={selectedBookingTypeId}
            onChange={handleBookingTypeChange}
            options={[
              { value: '', label: 'None (no booking CTA)' },
              ...bookingTypes.map((bt) => ({ value: bt.booking_type_id, label: bt.name })),
            ]}
            disabled={bookingSaving}
          />
        )}
        {bookingError && (
          <div className="fi-error" style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <AlertCircle size={14} /> {bookingError}
          </div>
        )}
        {bookingSuccess && (
          <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--success, #16a34a)' }}>
            {bookingSuccess}
          </div>
        )}
      </GlassCard>

      <GlassCard>
        <h4 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
          Repeat submissions
        </h4>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!form.merge_repeat_submissions}
            disabled={mergeSaving}
            onChange={(e) => toggleMerge(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Treat a repeat submission from the same email address as an update
            <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
              Instead of creating a second submission and CRM record, the earlier one is updated: new answers replace old ones,
              blank answers never erase earlier ones, and the previous version is kept. Needs an Email field (or an invite
              address) to match on. Leave off for lead forms where every submission should be a new lead.
            </span>
          </span>
        </label>
        {mergeError && (
          <div className="fi-error" style={{ marginTop: 'var(--space-2)' }}>
            <AlertCircle size={16} />
            {mergeError}
          </div>
        )}
      </GlassCard>
    </div>
  );
};

// ─── FormIntakeModule (main) ─────────────────────────────────────────────────

type TabKey = 'upload' | 'forms' | 'editor' | 'style' | 'crm' | 'invites' | 'public-link' | 'submissions';

const FormIntakeModule: React.FC = () => {
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const [activeTab, setActiveTab] = useState<TabKey>('forms');
  const [forms, setForms] = useState<FormIntakeForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formsError, setFormsError] = useState('');
  const [selectedForm, setSelectedForm] = useState<FormIntakeForm | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const loadForms = useCallback(async () => {
    setFormsLoading(true);
    setFormsError('');
    try {
      const data = await formIntakeApi.listForms();
      setForms(data);
      setSelectedForm((prev) => {
        if (!prev) return prev;
        const updated = data.find((f) => f.form_id === prev.form_id);
        return updated ?? prev;
      });
    } catch (err: any) {
      setFormsError(getApiError(err, 'Failed to load forms.'));
    } finally {
      setFormsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Generous cap: a large "Generate CRM & Publish" form creates many SuiteCRM modules (one per
  // repeating section), each with a Quick Repair — ~75s+ observed for an 18-section form. Poll every
  // 2s up to 300s so the UI reflects completion instead of timing out mid-provision.
  const MAX_POLL_TICKS = 150; // 150 × 2s = 300s

  const startPolling = (formId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollCountRef.current = 0;
    pollRef.current = setInterval(async () => {
      pollCountRef.current++;
      if (pollCountRef.current > MAX_POLL_TICKS) {
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }
      try {
        const updated = await formIntakeApi.getForm(formId);
        setForms((prev) => prev.map((f) => (f.form_id === formId ? updated : f)));
        setSelectedForm((prev) => (prev?.form_id === formId ? updated : prev));
        if (updated.status !== 'provisioning') {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        // ignore poll errors
      }
    }, 2000);
  };

  // Throws on failure so callers can surface the error appropriately: the local Publish / Unpublish
  // buttons alert (FormsTab.handlePublish), and the company-target confirm modal shows it inline.
  const handlePublishToggle = async (form: FormIntakeForm) => {
    if (form.status === 'published') {
      await formIntakeApi.unpublish(form.form_id);
      await loadForms();
    } else {
      await formIntakeApi.publish(form.form_id);
      startPolling(form.form_id);
      await loadForms();
    }
  };

  const handleDelete = async (form: FormIntakeForm) => {
    try {
      await formIntakeApi.deleteForm(form.form_id);
      setForms((prev) => prev.filter((f) => f.form_id !== form.form_id));
      setSelectedForm((prev) => (prev?.form_id === form.form_id ? null : prev));
    } catch (err: any) {
      const msg = getApiError(err, 'Delete failed.');
      alert(msg);
    }
  };

  const handleGenerateAndPublish = async (form: FormIntakeForm) => {
    try {
      await formIntakeApi.generateAndPublish(form.form_id);
      // Optimistically mark as provisioning while the backend works
      setForms((prev) =>
        prev.map((f) => (f.form_id === form.form_id ? { ...f, status: 'provisioning' } : f))
      );
      setSelectedForm((prev) =>
        prev?.form_id === form.form_id ? { ...prev, status: 'provisioning' } : prev
      );
      startPolling(form.form_id);
    } catch (err: any) {
      alert(getApiError(err, 'Generate & publish failed.'));
    }
  };

  const handleUploaded = (form: FormIntakeForm) => {
    setSelectedForm(form);
    setForms((prev) => {
      const exists = prev.find((f) => f.form_id === form.form_id);
      if (exists) return prev.map((f) => (f.form_id === form.form_id ? form : f));
      return [form, ...prev];
    });
    setActiveTab('editor');
  };

  const handleFormSaved = (updated: FormIntakeForm) => {
    setSelectedForm(updated);
    setForms((prev) => prev.map((f) => (f.form_id === updated.form_id ? updated : f)));
  };

  const goToEditor = (form: FormIntakeForm) => {
    setSelectedForm(form);
    setActiveTab('editor');
  };

  const goToInvites = (form: FormIntakeForm) => {
    setSelectedForm(form);
    setActiveTab('invites');
  };

  const goToPublicLink = (form: FormIntakeForm) => {
    setSelectedForm(form);
    setActiveTab('public-link');
  };

  const goToSubmissions = (form: FormIntakeForm) => {
    setSelectedForm(form);
    setActiveTab('submissions');
  };

  const handleCreateForm = async () => {
    try {
      const form = await formIntakeApi.createForm(undefined, ctx?.selectedProjectId);
      setSelectedForm(form);
      setForms((prev) => {
        const exists = prev.find((f) => f.form_id === form.form_id);
        if (exists) return prev.map((f) => (f.form_id === form.form_id ? form : f));
        return [form, ...prev];
      });
      setActiveTab('editor');
    } catch (err: any) {
      alert(getApiError(err, 'Failed to create form.'));
    }
  };

  const handleTabChange = (id: string) => {
    if (['editor', 'style', 'crm', 'invites', 'public-link', 'submissions'].includes(id) && !selectedForm) return;
    setActiveTab(id as TabKey);
  };

  const tabs = [
    { id: 'upload', label: 'Upload' },
    { id: 'forms', label: 'Forms' },
    { id: 'editor', label: 'Editor' },
    { id: 'style', label: 'Style' },
    { id: 'crm', label: 'CRM' },
    { id: 'invites', label: 'Invites' },
    { id: 'public-link', label: 'Public Link' },
    { id: 'submissions', label: 'Submissions' },
  ];

  return (
    <div className="form-intake-module">
      <ModuleHeader title="CRM Form Builder" />

      <div className="form-intake-tabs-bar">
        <GlassTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
      </div>

      {selectedForm && activeTab !== 'upload' && activeTab !== 'forms' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Editing:</span>
          <strong style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{selectedForm.name}</strong>
          <GlassBadge variant={STATUS_VARIANTS[selectedForm.status] ?? 'default'}>
            {selectedForm.status}
          </GlassBadge>
        </div>
      )}

      {activeTab === 'upload' && <UploadTab onUploaded={handleUploaded} projectId={ctx?.selectedProjectId} />}

      {activeTab === 'forms' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
            <GlassButton variant="primary" onClick={handleCreateForm}>
              New form
            </GlassButton>
          </div>
          <FormsTab
            forms={forms}
            loading={formsLoading}
            error={formsError}
            onRefresh={loadForms}
            onEdit={goToEditor}
            onInvites={goToInvites}
            onPublicLink={goToPublicLink}
            onSubmissions={goToSubmissions}
            onPublishToggle={handlePublishToggle}
            onDelete={handleDelete}
            onGenerateAndPublish={handleGenerateAndPublish}
          />
        </>
      )}

      {activeTab === 'editor' && selectedForm && (
        <EditorTab form={selectedForm} onSaved={handleFormSaved} />
      )}

      {activeTab === 'style' && selectedForm && (
        <FormStyleTab form={selectedForm} onSaved={handleFormSaved} projectId={selectedForm.project_id ?? ctx?.selectedProjectId} />
      )}

      {activeTab === 'crm' && selectedForm && (
        <CRMTab form={selectedForm} onSaved={handleFormSaved} />
      )}

      {activeTab === 'invites' && selectedForm && (
        <InvitesTab form={selectedForm} />
      )}

      {activeTab === 'public-link' && selectedForm && (
        <PublicLinkTab form={selectedForm} onUpdated={handleFormSaved} />
      )}

      {activeTab === 'submissions' && selectedForm && (
        <SubmissionsTab form={selectedForm} />
      )}

      {!selectedForm && ['editor', 'style', 'crm', 'invites', 'public-link', 'submissions'].includes(activeTab) && (
        <div className="fi-empty-state">
          <div className="fi-empty-icon"><FileText size={48} /></div>
          <h3 className="fi-empty-title">No form selected</h3>
          <p className="fi-empty-desc">Go to the Forms tab and click Edit on a form first.</p>
          <GlassButton variant="secondary" onClick={() => setActiveTab('forms')}>
            Go to Forms
          </GlassButton>
        </div>
      )}
    </div>
  );
};

export default FormIntakeModule;
